import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { PageHero, Reveal } from "../components/Reveal";
import { CONTACT, SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under ₹25,000", "₹25,000 – ₹50,000", "₹50,000 – ₹1,00,000", "₹1,00,000 – ₹3,00,000", "₹3,00,000+"];

const CONTACT_CARDS = [
  { icon: MessageCircle, label: "WhatsApp Us", value: CONTACT.phone, href: CONTACT.whatsapp, testid: "contact-whatsapp-card" },
  { icon: Mail, label: "Email Us", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-email-card" },
  { icon: Phone, label: "Call Us", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-call-card" },
  { icon: MapPin, label: "Visit Us", value: CONTACT.location, href: null, testid: "contact-location-card" },
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder-slate-500 outline-none transition-all duration-300 focus:border-dg-cyan/60 focus:bg-white/[0.06] focus:shadow-[0_0_20px_rgba(0,184,212,0.12)]";

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    service: params.get("service") || "", budget: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received! Our team will get back to you within 24 hours.");
      setForm({ name: "", company: "", email: "", phone: "", service: "", budget: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Contact Us"
        title="LET'S BUILD SOMETHING THAT GROWS YOUR BUSINESS."
        sub="Tell us about your business, your goals and the digital challenge you're trying to solve. Our team will get back to you within 24 hours."
      />

      <section className="bg-dg-navy pb-24 sm:pb-32">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
          <div className="space-y-4">
            {CONTACT_CARDS.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                {c.href ? (
                  <a
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={c.testid}
                    className="group flex items-center gap-5 rounded-2xl border border-white/8 bg-white/[0.03] p-6 transition-all duration-300 hover:border-dg-cyan/40 hover:bg-white/[0.06]"
                  >
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white"><c.icon size={20} /></span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{c.label}</span>
                      <span className="mt-1 block font-display text-base font-bold text-white group-hover:text-dg-cyan transition-colors">{c.value}</span>
                    </span>
                  </a>
                ) : (
                  <div data-testid={c.testid} className="flex items-center gap-5 rounded-2xl border border-white/8 bg-white/[0.03] p-6">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-dg-blue to-dg-cyan text-white"><c.icon size={20} /></span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{c.label}</span>
                      <span className="mt-1 block font-display text-base font-bold text-white">{c.value}</span>
                    </span>
                  </div>
                )}
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <div className="rounded-2xl border border-dg-cyan/20 bg-gradient-to-br from-dg-blue/20 to-transparent p-6">
                <p className="font-display text-lg font-bold text-white">Not a form person?</p>
                <p className="mt-1.5 text-sm text-slate-400">WhatsApp us directly — a strategist replies, not a bot.</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-10 backdrop-blur"
            >
              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-white">Request a Consultation</h2>
              <p className="mt-1.5 text-sm text-slate-400">Free 30-minute strategy call. No obligation.</p>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <input data-testid="form-name" required minLength={2} placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="form-company" placeholder="Company Name" value={form.company} onChange={set("company")} className={inputCls} />
                <input data-testid="form-email" required type="email" placeholder="Email Address *" value={form.email} onChange={set("email")} className={inputCls} />
                <input data-testid="form-phone" required minLength={7} placeholder="Phone Number *" value={form.phone} onChange={set("phone")} className={inputCls} />
                <select data-testid="form-service" required value={form.service} onChange={set("service")} className={`${inputCls} appearance-none [&>option]:bg-dg-navy`}>
                  <option value="" disabled>Select Service *</option>
                  {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                  <option value="Digital Marketing">Digital Marketing (Complete)</option>
                  <option value="Other">Other</option>
                </select>
                <select data-testid="form-budget" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none [&>option]:bg-dg-navy`}>
                  <option value="" disabled={form.budget !== ""}>Budget Range</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <textarea
                data-testid="form-message"
                required
                minLength={5}
                rows={5}
                placeholder="Project Details — your goals, timeline and challenges *"
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} mt-5 resize-none`}
              />
              <button
                data-testid="form-submit-btn"
                type="submit"
                disabled={loading}
                className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-dg-cyan px-8 py-4 font-bold text-dg-navy transition-all duration-300 hover:bg-white hover:shadow-[0_0_35px_rgba(0,184,212,0.45)] disabled:opacity-60 sm:w-auto"
              >
                {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />}
                {loading ? "Sending..." : "Request a Consultation"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
